import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, Store, MapPin, Phone } from "lucide-react";
import { api, gbp } from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";

/**
 * Landing after the COMBINED multi-retailer Stripe Checkout. Polls
 * GET /api/payments/status/{session_id} until paid, then loads the tokenised
 * order (GET /api/orders/{order_id}?token=...) and shows the collections
 * grouped by shop. Retailer identity is only revealed once paid.
 */
export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id") || "";
  const [phase, setPhase] = useState("waiting"); // waiting | paid | email-only | failed
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const cancelled = useRef(false);

  useEffect(() => {
    if (!sid) { setPhase("failed"); return; }
    cancelled.current = false;
    let timer;

    async function loadOrder(orderId) {
      let stash = null;
      try {
        const raw = window.sessionStorage.getItem(`fm.order.${sid}`);
        stash = raw ? JSON.parse(raw) : null;
      } catch (err) {
        console.warn("FloorMatch: couldn't read stashed order token.", err);
      }
      const id = orderId || stash?.order_id;
      const token = stash?.access_token;
      if (!id || !token) { setPhase("email-only"); return; }
      try {
        const r = await api.get(`/orders/${id}?token=${encodeURIComponent(token)}`);
        setOrder(r.data.order);
        setPhase("paid");
      } catch (e) {
        setPhase("email-only");
      }
    }

    async function poll(n) {
      if (cancelled.current) return;
      setAttempts(n);
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          await loadOrder(r.data.order_id);
          return;
        }
        if (["expired", "async_payment_failed"].includes(r.data.payment_status) || r.data.status === "failed") {
          setPhase("failed");
          return;
        }
      } catch (e) { /* transient */ }
      if (n >= 20) { setPhase("email-only"); return; }
      timer = setTimeout(() => poll(n + 1), 1500);
    }
    poll(1);
    return () => { cancelled.current = true; if (timer) clearTimeout(timer); };
  }, [sid]);

  return (
    <>
      <PublicHeader />
      <main className="min-h-screen bg-slate-50" data-testid="order-success">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
          {phase === "waiting" && (
            <div className="text-center py-16" data-testid="order-waiting">
              <Loader2 className="w-10 h-10 text-emerald-800 mx-auto animate-spin" />
              <h1 className="font-display font-bold text-2xl text-slate-900 mt-4">Confirming your payment…</h1>
              <p className="text-slate-600 mt-2 text-sm">Waiting on Stripe. This usually takes a second or two.{attempts > 6 && " (Still waiting — hang tight.)"}</p>
            </div>
          )}

          {phase === "paid" && order && (
            <div data-testid="order-paid">
              <div className="text-center">
                <CheckCircle2 className="w-12 h-12 text-emerald-700 mx-auto" />
                <h1 className="font-display font-bold text-3xl text-slate-900 mt-4">Payment received</h1>
                <p className="text-slate-600 mt-2">Order <span className="font-mono font-semibold">{order.reference}</span> · paid in full</p>
                <p className="text-slate-500 text-sm mt-1">Total {gbp(order.total_gbp)} · {order.collections?.length} collection point{order.collections?.length !== 1 ? "s" : ""}</p>
              </div>

              <div className="mt-8 space-y-4">
                {order.collections?.map((c, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={`order-collection-${i}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                        <Store className="w-5 h-5 text-emerald-800" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h2 className="font-display font-semibold text-lg text-slate-900">{c.shop?.name || `Collection point ${i + 1}`}</h2>
                        {c.shop?.address && (
                          <p className="text-sm text-slate-600 flex items-start gap-1.5 mt-0.5"><MapPin className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />{c.shop.address}{c.shop.postcode ? `, ${c.shop.postcode}` : ""}</p>
                        )}
                        {c.shop?.phone && (
                          <p className="text-sm text-slate-600 flex items-center gap-1.5 mt-0.5"><Phone className="w-4 h-4 shrink-0 text-slate-400" />{c.shop.phone}</p>
                        )}
                        <div className="mt-3 divide-y divide-slate-100 border-t border-slate-100">
                          {c.items.map((it, j) => (
                            <div key={j} className="py-2 flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <div className="font-medium text-slate-800 truncate">{it.title}</div>
                                <div className="text-xs text-slate-500">{it.room_name} · ref {it.listing_reference}</div>
                              </div>
                              <div className="font-semibold text-[#0F3D2E] shrink-0">{gbp(it.price_gbp)}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900">
                Bring your order reference <span className="font-mono font-semibold">{order.reference}</span> and photo ID to each shop to collect. We've emailed these details too.
                {order.collection_window_ends_at && <> Collect within your reservation window.</>}
              </div>

              <div className="text-center mt-6">
                <Link to="/" className="fm-btn-primary inline-block" data-testid="order-home">Back to home</Link>
              </div>
            </div>
          )}

          {phase === "email-only" && (
            <div className="text-center py-16" data-testid="order-check-email">
              <CheckCircle2 className="w-10 h-10 text-emerald-700 mx-auto" />
              <h1 className="font-display font-bold text-2xl text-slate-900 mt-4">Payment received</h1>
              <p className="text-slate-600 mt-2 text-sm">We've emailed your collection details for every shop in your order.</p>
              <Link to="/" className="fm-btn-primary inline-block mt-6" data-testid="order-home">Back to home</Link>
            </div>
          )}

          {phase === "failed" && (
            <div className="text-center py-16" data-testid="order-failed">
              <AlertCircle className="w-10 h-10 text-red-600 mx-auto" />
              <h1 className="font-display font-bold text-2xl text-slate-900 mt-4">Payment didn't go through</h1>
              <p className="text-slate-600 mt-2 text-sm">No charge was made and every item has been released back to the marketplace. Your plan is saved — you can try again.</p>
              <Link to="/whole-home" className="fm-btn-primary inline-block mt-6" data-testid="order-back-plan">Back to my plan</Link>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
